import { useEffect, useState } from "react";
import { Rocket, Lock, Loader2, Trash2, MessageSquarePlus, ThumbsUp, AlertTriangle, Lightbulb } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Mono = ({ children, className = "" }) => (
  <span className={`font-mono uppercase tracking-[0.2em] ${className}`}>{children}</span>
);
const inp = "bg-navy-950 border border-gold-500/25 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-gold-500/60";

const PHASE_STYLE = {
  pending: "border-white/15 text-white/40",
  active: "border-gold-500 text-gold-300 bg-gold-500/10",
  done: "border-emerald-400/50 text-emerald-300 bg-emerald-400/5",
};
const SENTIMENTS = [
  ["positive", "Positive", ThumbsUp, "text-emerald-300 border-emerald-400/40"],
  ["issue", "Issue", AlertTriangle, "text-red-300 border-red-400/40"],
  ["idea", "Idea", Lightbulb, "text-sky-300 border-sky-400/40"],
];

export default function AlphaHQ() {
  const { user } = useAuth();
  const isStaff = ["admin", "staff"].includes(user?.role);
  const [data, setData] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [fb, setFb] = useState({ note: "", author: "", phase_idx: "", sentiment: "positive" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/alpha/program").then((r) => {
    setData(r.data);
    setSelectedId((id) => id || r.data.programs?.[0]?.id || null);
  }).catch(() => {});
  useEffect(() => { if (isStaff) load(); }, [isStaff]); // eslint-disable-line

  if (!isStaff) {
    return (
      <div className="text-white max-w-lg mx-auto text-center py-24" data-testid="alpha-restricted">
        <Lock className="h-10 w-10 text-gold-400 mx-auto mb-5" />
        <h1 className="font-heading font-light text-3xl">Restricted</h1>
        <p className="text-white/50 font-light mt-3">Alpha HQ is visible to administrators only.</p>
      </div>
    );
  }
  if (!data) return <div className="text-white/40 text-sm" data-testid="alpha-loading">Loading Alpha HQ…</div>;

  const { programs, feedback } = data;
  const program = programs.find((p) => p.id === selectedId) || programs[0];
  if (!program) return <div className="text-white/40 text-sm" data-testid="alpha-loading">No alpha programs.</div>;
  const doneCount = program.phases.filter((p) => p.status === "done").length;
  const progFeedback = feedback.filter((f) => (f.program_id || programs[0]?.id) === program.id);

  const setProgram = (p) => setData((d) => ({ ...d, programs: d.programs.map((x) => (x.id === p.id ? p : x)) }));
  const patchProgram = async (body) => { const { data: p } = await api.patch(`/alpha/program/${program.id}`, body); setProgram(p); };
  const setPhase = async (idx, status) => { const { data: p } = await api.patch(`/alpha/program/${program.id}/phases/${idx}`, { status }); setProgram(p); };

  const addFeedback = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/alpha/feedback", {
        note: fb.note, author: fb.author || null, sentiment: fb.sentiment,
        phase_idx: fb.phase_idx === "" ? null : Number(fb.phase_idx),
        program_id: program.id,
      });
      setFb((f) => ({ ...f, note: "" }));
      load();
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-8 text-white" data-testid="alpha-hq-page">
      <div>
        <Mono className="text-gold-400 text-[0.65rem] inline-flex items-center gap-2"><Rocket className="h-3 w-3" /> Alpha programs · commercial in confidence</Mono>
        <h1 className="font-heading font-light text-4xl tracking-tight mt-3">Alpha HQ</h1>
      </div>

      <div className="flex items-center gap-2 flex-wrap" data-testid="alpha-program-tabs">
        {programs.map((p) => (
          <button key={p.id} onClick={() => setSelectedId(p.id)} data-testid={`alpha-program-tab-${p.state || p.id}`}
            className={`inline-flex items-center gap-2 border px-4 py-2.5 text-sm transition-colors ${p.id === program.id ? "border-gold-500 text-gold-300 bg-gold-500/10" : "border-gold-500/20 text-white/55 hover:text-gold-300"}`}>
            <Mono className="text-[0.5rem]">{p.state}</Mono> {p.council}
          </button>
        ))}
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <p className="text-white/55 font-light">
          {program.council} · {program.contact} — led by {program.lead}. {doneCount}/{program.phases.length} phases complete.
        </p>
        <div className="flex items-center gap-3">
          <div>
            <label className="font-mono uppercase tracking-[0.18em] text-white/45 text-[0.55rem] block mb-1">Program status</label>
            <select value={program.status} onChange={(e) => patchProgram({ status: e.target.value })} data-testid="alpha-status-select" className={inp}>
              {data.program_statuses.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
            </select>
          </div>
          <div>
            <label className="font-mono uppercase tracking-[0.18em] text-white/45 text-[0.55rem] block mb-1">Start date</label>
            <input type="date" value={program.start_date || ""} onChange={(e) => patchProgram({ start_date: e.target.value })} data-testid="alpha-start-date" className={inp} />
          </div>
        </div>
      </div>

      {/* phase tracker */}
      <div className="space-y-3" data-testid="alpha-phases">
        {program.phases.map((p) => (
          <div key={p.idx} data-testid={`alpha-phase-${p.idx}`}
            className={`border p-5 flex flex-wrap items-start gap-5 ${PHASE_STYLE[p.status]} bg-navy-900/40`}>
            <div className="font-mono text-2xl text-gold-400/70 w-8">{p.idx}</div>
            <div className="flex-1 min-w-[220px]">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-heading text-lg text-white">{p.name}</span>
                <Mono className="text-[0.5rem] text-white/40">{p.timeline}</Mono>
                {p.released_on && <Mono className="text-[0.5rem] text-emerald-300/80">released {p.released_on}</Mono>}
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2.5">
                {p.modules.map((m) => (
                  <span key={m} className="text-[0.65rem] font-light text-white/55 border border-white/10 px-2 py-0.5 rounded-sm">{m}</span>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-1">
              {["pending", "active", "done"].map((s) => (
                <button key={s} onClick={() => setPhase(p.idx, s)} data-testid={`alpha-phase-${p.idx}-${s}`}
                  className={`px-3 py-1.5 text-xs border transition-colors ${p.status === s ? PHASE_STYLE[s] + " font-semibold" : "border-white/10 text-white/35 hover:text-gold-300"}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* feedback log */}
      <div className="grid lg:grid-cols-2 gap-6">
        <form onSubmit={addFeedback} className="border border-gold-500/25 bg-navy-900/40 p-5 space-y-3 h-fit" data-testid="alpha-feedback-form">
          <Mono className="text-gold-400 text-[0.6rem] inline-flex items-center gap-2"><MessageSquarePlus className="h-3.5 w-3.5" /> Log feedback · {program.council}</Mono>
          <div className="grid grid-cols-2 gap-3">
            <input value={fb.author} onChange={(e) => setFb((f) => ({ ...f, author: e.target.value }))} placeholder={`Author (e.g. ${program.contact})`} data-testid="alpha-feedback-author" className={inp + " w-full"} />
            <select value={fb.phase_idx} onChange={(e) => setFb((f) => ({ ...f, phase_idx: e.target.value }))} data-testid="alpha-feedback-phase" className={inp + " w-full"}>
              <option value="">General</option>
              {program.phases.map((p) => <option key={p.idx} value={p.idx}>{`Phase ${p.idx} — ${p.name}`}</option>)}
            </select>
          </div>
          <div className="flex gap-1.5">
            {SENTIMENTS.map(([v, label, Icon, cls]) => (
              <button type="button" key={v} onClick={() => setFb((f) => ({ ...f, sentiment: v }))} data-testid={`alpha-sentiment-${v}`}
                className={`inline-flex items-center gap-1.5 border px-3 py-1.5 text-xs rounded-sm ${fb.sentiment === v ? cls + " bg-white/5" : "border-white/10 text-white/35"}`}>
                <Icon className="h-3.5 w-3.5" /> {label}
              </button>
            ))}
          </div>
          <textarea rows={3} required value={fb.note} onChange={(e) => setFb((f) => ({ ...f, note: e.target.value }))}
            placeholder="What did the crews say? What broke? What should we build?" data-testid="alpha-feedback-note" className={inp + " w-full"} />
          <button type="submit" disabled={busy || !fb.note.trim()} data-testid="alpha-feedback-save"
            className="w-full bg-gold-500 text-navy-950 font-semibold py-2.5 rounded-sm hover:bg-gold-400 disabled:opacity-50 flex items-center justify-center gap-2">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save entry
          </button>
        </form>

        <div className="space-y-3" data-testid="alpha-feedback-list">
          <Mono className="text-white/45 text-[0.6rem]">Feedback log · {program.council} ({progFeedback.length})</Mono>
          {progFeedback.length === 0 ? (
            <div className="border border-dashed border-gold-500/25 p-10 text-center text-white/40 font-light text-sm" data-testid="alpha-feedback-empty">
              Nothing logged yet — every crew comment and council remark goes here.
            </div>
          ) : (
            progFeedback.map((f) => {
              const s = SENTIMENTS.find(([v]) => v === f.sentiment) || SENTIMENTS[0];
              const Icon = s[2];
              return (
                <div key={f.id} data-testid={`alpha-feedback-${f.id}`} className="border border-gold-500/20 bg-navy-900/40 p-4 flex items-start gap-3">
                  <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${s[3].split(" ")[0]}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white text-sm">{f.author}</span>
                      {f.phase_idx != null && <Mono className="text-[0.5rem] text-gold-400/70">Phase {f.phase_idx}</Mono>}
                      <span className="text-white/30 text-[0.6rem] font-mono">{(f.created_at || "").slice(0, 10)}</span>
                    </div>
                    <p className="text-white/60 font-light text-sm mt-1">{f.note}</p>
                  </div>
                  <button onClick={async () => { await api.delete(`/alpha/feedback/${f.id}`); load(); }}
                    data-testid={`alpha-feedback-del-${f.id}`} className="text-white/25 hover:text-red-400 shrink-0">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
